import DuaPageComponent from '@components/DuaPage/DuaPageComponent'
import Breadcrumb from '@components/shared/Breadcrumb/Breadcrumb'
import LoadingSpinner from '@components/shared/Loading/Loading'
import { useAuth } from '@contexts/AuthContext'
import type { CommunityDua } from '../../types/communityDua'
import Link from 'next/link'
import { DefaultSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { GetServerSideProps } from 'next'
import useAutoScroll from '@hooks/useAutoScroll'
import CommunityDuaHeader from '@components/CommunityDua/CommunityDuaHeader'
import { FaArrowLeft } from 'react-icons/fa'
import { SITE_URL } from '../../lib/sitemap'

const STATUS_LABELS: Record<string, string> = {
  private: 'Sadece bende',
  pending: 'Onay bekliyor',
  approved: 'Yayında',
}

type CommunityDuaDetailPageProps = {
  id: string
  /** Approved dua loaded on the server so search engines see its content; null otherwise. */
  initialDua: CommunityDua | null
}

/** Tells search engines not to index this page (private, pending or missing duas). */
const NO_INDEX_TAGS = [{ name: 'robots', content: 'noindex,nofollow' }]

/** Shows one community dua; approved ones arrive server-rendered, others load in the browser. */
export default function CommunityDuaDetailPage({
  id: initialId,
  initialDua,
}: CommunityDuaDetailPageProps) {
  const router = useRouter()
  const { id: queryId } = router.query
  const id = (queryId as string) || initialId
  const { user } = useAuth()
  const [dua, setDua] = useState<CommunityDua | null>(initialDua)
  const [loading, setLoading] = useState(!initialDua)
  const [error, setError] = useState(false)
  const [liking, setLiking] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [userIsAdmin, setUserIsAdmin] = useState(false)

  const { isScrolling, isPending, toggleScroll, duration, setDuration } =
    useAutoScroll('sizden-gelen-dualar')

  const checkAdmin = useCallback(async () => {
    if (!user?.uid) return
    try {
      const res = await fetch(`/api/is-admin?userId=${user.uid}`)
      const data = await res.json()
      setUserIsAdmin(data.isAdmin)
    } catch {
      setUserIsAdmin(false)
    }
  }, [user?.uid])

  useEffect(() => {
    void checkAdmin()
  }, [checkAdmin])

  const fetchDua = useCallback(async () => {
    if (typeof id !== 'string') return
    setLoading(true)
    setError(false)
    try {
      const params = new URLSearchParams()
      if (user?.uid) params.set('userId', user.uid)
      const res = await fetch(`/api/community-duas/${id}?${params.toString()}`)
      if (!res.ok) {
        // An approved dua from the server stays visible if the refresh fails.
        if (!initialDua) {
          setError(true)
          setDua(null)
        }
        return
      }
      const data = await res.json()
      setDua(data)
    } catch {
      if (!initialDua) {
        setError(true)
        setDua(null)
      }
    } finally {
      setLoading(false)
    }
  }, [id, initialDua, user?.uid])

  useEffect(() => {
    if (id) void fetchDua()
  }, [fetchDua, id])

  const handleLike = async () => {
    if (!user?.uid || !dua || liking) return
    setLiking(true)
    try {
      const res = await fetch(`/api/community-duas/${dua.id}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid }),
      })
      if (res.ok) {
        const data = await res.json()
        setDua((prev) =>
          prev
            ? {
                ...prev,
                likes: data.liked
                  ? [...prev.likes, user.uid]
                  : prev.likes.filter((uid) => uid !== user.uid),
                likeCount: data.likeCount ?? prev.likeCount,
              }
            : null,
        )
      }
    } finally {
      setLiking(false)
    }
  }

  const handleDelete = async () => {
    if (!user?.uid || !dua || deleting) return
    if (!window.confirm('Bu duayı silmek istediğinize emin misiniz?')) return

    setDeleting(true)
    try {
      const res = await fetch(`/api/community-duas/${dua.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid }),
      })
      if (res.ok) {
        toast.success('Dua silindi.')
        router.push('/sizden-gelen-dualar')
      } else {
        const data = await res.json().catch(() => ({}))
        toast.error(data.error || 'Silme işlemi başarısız.')
      }
    } catch {
      toast.error('Bir hata oluştu.')
    } finally {
      setDeleting(false)
    }
  }

  if (loading && !dua) {
    return (
      <>
        <DefaultSeo additionalMetaTags={NO_INDEX_TAGS} />
        <LoadingSpinner />
      </>
    )
  }

  if (error || !dua) {
    return (
      <>
        <DefaultSeo additionalMetaTags={NO_INDEX_TAGS} />
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-center px-4 py-24 text-center">
          <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-danger/10 text-danger shadow-xl shadow-danger/10">
            <FaArrowLeft size={32} />
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-main">Dua Bulunamadı</h2>
          <p className="mt-2 text-muted max-w-xs">
            Dua silinmiş olabilir veya bu duayı görüntüleme yetkiniz yok.
          </p>
          <Link
            href="/sizden-gelen-dualar"
            className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-brand-secondary px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-secondary/20 transition-all hover:scale-105 hover:bg-brand-secondary/90 active:scale-95"
          >
            <FaArrowLeft size={14} />
            Listeye Geri Dön
          </Link>
        </div>
      </>
    )
  }

  const breadcrumbItems = [
    { label: 'Dualar', href: '/dualar' },
    { label: 'Sizden Gelen Dualar', href: '/sizden-gelen-dualar' },
    { label: dua.title },
  ]

  const isAuthor = user?.uid === dua.userId
  const isLiked = !!(user && dua.likes.includes(user.uid))

  return (
    <>
      <DefaultSeo
        title={`${dua.title} | Sizden Gelen Dualar | Hayırhah`}
        description={
          dua.description
            ? `${dua.title} - ${dua.description}`
            : `${dua.title} - Topluluk duası`
        }
        canonical={
          dua.status === 'approved' && dua.slug
            ? `${SITE_URL}/sizden-gelen-dualar/${dua.slug}`
            : undefined
        }
        additionalMetaTags={dua.status === 'approved' ? undefined : NO_INDEX_TAGS}
      />
      <div className="mx-auto w-full max-w-3xl pb-12">
        <Breadcrumb items={breadcrumbItems} />

        <CommunityDuaHeader
          dua={dua}
          isAuthor={isAuthor}
          userIsAdmin={userIsAdmin}
          isLiked={isLiked}
          liking={liking}
          onLike={handleLike}
          onDelete={handleDelete}
          deleting={deleting}
          isScrolling={isScrolling}
          isPending={isPending}
          toggleScroll={toggleScroll}
          duration={duration}
          setDuration={setDuration}
          statusLabels={STATUS_LABELS}
        />

        <div className="mt-4 rounded-3xl bg-bg-main">
          <DuaPageComponent
            type="sizden-gelen-dualar"
            page={1}
            noBookmark
            data={dua.parts}
          />
        </div>
      </div>
    </>
  )
}

/**
 * Loads approved duas on the server so their title and text are in the HTML.
 * Unknown IDs return a real 404; private and pending duas still load in the browser.
 */
export const getServerSideProps: GetServerSideProps<CommunityDuaDetailPageProps> = async (
  context,
) => {
  const id = typeof context.params?.id === 'string' ? context.params.id : ''
  const { lookupCommunityDuaForPage } =
    await import('../../lib/communityDuaPublic.server')
  const lookup = await lookupCommunityDuaForPage(id)
  if (!lookup.found) {
    return { notFound: true }
  }
  return { props: { id, initialDua: lookup.publicDua } }
}
